// @owners { team: patients }
import { SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  ActionSheetV2,
  Description,
  Icon,
  InlineAlert,
  LgSpacing,
  ListItem,
  MdPadding,
  MdSpacing,
  ThirdPartyIcon,
} from '@alto/design-system';
import { Experimentation } from '@alto/experimentation';
import React, { useContext, useState } from 'react';
import { Platform } from 'react-native';
import { type PaymentMethodType } from '~shared/constants';
import { useAnalytics, useIsFeatureDisabledForCompany } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { AddPaymentCardActionSheet } from './AddPaymentCardActionSheet';
import { AddPaymentCardPaymentSheet } from './AddPaymentCardPaymentSheet';
import { PlaidListNavigation } from './PlaidListNavigation';

type Props = {
  readonly handleNativePay?: (paymentMethodType: PaymentMethodType) => void;
  readonly paymentMethodType: PaymentMethodType;
};

export const AddPaymentMethodActionSheet = ({ handleNativePay, paymentMethodType }: Props) => {
  const { value: isAddApplePayDisabled } = Experimentation.useFeatureFlag('apple_pay_enabled');
  // With the PaymentSheet flag on, "Credit or Debit Card" presents Stripe's sheet
  // directly over this action sheet instead of swapping to the AddPaymentCard sheet,
  // which otherwise flashes empty for the moment before Stripe's sheet covers it.
  const { value: paymentSheetEnabled } = Experimentation.useFeatureFlag('stripe_payment_element_web');
  const { trackEvent } = useAnalytics();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [addingCard, setAddingCard] = useState(false);
  const [sheetPresented, setSheetPresented] = useState(false);
  const { closeActionSheet, setActiveActionSheet } = useContext(ActionSheetContext);
  const showApplePay = Platform.OS === 'ios' && !isAddApplePayDisabled;
  const { isFeatureDisabled: shouldDisablePlaid, isLoading } = useIsFeatureDisabledForCompany('plaid_bank_account');

  const handlePaymentCardPress = () => {
    if (addingCard) return;
    trackEvent({
      event: EVENTS.ADD_PAYMENT_METHOD_TYPE_SELECTED,
      params: { type: 'credit_card' },
    });
    // The PaymentSheet is native-only; web (if it ever renders this shared sheet)
    // keeps the nested action sheet, whose AddPaymentCard gate handles the flag.
    if (paymentSheetEnabled && Platform.OS !== 'web') {
      setAddingCard(true);
    } else {
      setActiveActionSheet(<AddPaymentCardActionSheet paymentMethodType={paymentMethodType} />);
    }
  };

  const resetAddingCard = () => {
    setAddingCard(false);
    setSheetPresented(false);
  };

  const handleNativePayPress = () => {
    if (handleNativePay) {
      trackEvent({
        event: EVENTS.ADD_PAYMENT_METHOD_TYPE_SELECTED,
        params: { type: 'apple_pay' },
      });
      handleNativePay(paymentMethodType);
    }
  };

  return (
    <ActionSheetV2
      title="Add new method"
      analyticsName="add payment method"
    >
      {errorMessage ? (
        <MdPadding
          topPadding={SPACING.STATIC.NONE}
          bottomPadding={SPACING.STATIC.NONE}
        >
          <InlineAlert type="error">
            <Description>{errorMessage}</Description>
          </InlineAlert>
          <MdSpacing />
        </MdPadding>
      ) : null}
      {addingCard ? (
        // Headless: drives Stripe's sheet (which slides up over this action sheet)
        // while the list below stays put - the tapped row shows the loading state
        // until the sheet is on screen. Canceling the sheet returns here.
        <AddPaymentCardPaymentSheet
          presentation="headless"
          paymentMethodType={paymentMethodType}
          onClose={closeActionSheet}
          onCancel={resetAddingCard}
          onPresented={() => {
            setSheetPresented(true);
          }}
          onStripeUnavailable={() => {
            resetAddingCard();
            setActiveActionSheet(
              <AddPaymentCardActionSheet
                paymentMethodType={paymentMethodType}
                forceLegacy
              />,
            );
          }}
        />
      ) : null}
      <ListItem
        LeftContent={<Icon name="creditcard" />}
        title="Credit or Debit Card"
        RightContent={<Icon name="chevronright-small" />}
        onPress={handlePaymentCardPress}
        loading={addingCard && !sheetPresented}
      />
      {showApplePay ? (
        <ListItem
          LeftContent={<ThirdPartyIcon name="applepay" />}
          RightContent={<Icon name="chevronright-small" />}
          title="Apple Pay"
          onPress={handleNativePayPress}
        />
      ) : null}
      {!shouldDisablePlaid && !isLoading && (
        <PlaidListNavigation
          paymentMethodType={paymentMethodType}
          setErrorMessage={setErrorMessage}
        />
      )}
      <LgSpacing />
    </ActionSheetV2>
  );
};
